import { useReducer, FC, useEffect, useState } from 'react'
import { EmployeeList } from './EmployeeList'
import { EmployeeLevelEnum, EmployeeSubordinateDto, UserInfo, BusinessUnit, TargetBonus, Salary, SkillEmployeeStatusDto, Country } from '../../types'
import { EmployeeFilterModal, CurrencyAndCountry } from './FilterModal/EmployeeFilterModal'
import {
  filterTableData,
  emptyFormData,
  EmployeeInfoBoxFilter,
  employeeTabInitialFilter,
  EmployeeModalFormDataType,
  filterSubordinatesEmployeesFromInfoBoxFilters,
  getOldestSyncUTCDate,
  possibleOffices,
} from './util'
import { LoadingAndErrors } from '../../components/custom_components/LoadingAndErrors'
import { useAuth } from '../../context/auth-context'
import { EmployeeInfoBox } from './EmployeeInfoBox'
import { PerInfoBoxActionButtonElement } from '../../components/custom_components/InfoBox/InfoBoxActionButtonElement'
import { useTranslation } from 'react-i18next'
import { useEmployeeStore } from './EmployeeStore'
import shallow from 'zustand/shallow'
import useSubordinates from '../../graphql/subordinates/useSubordinates'
import useGetCountriesWithBranchOfficeVisibleFlag from '../../graphql/countrySettings/hooks/useGetCountriesWithBranchOfficeVisibleFlag'

export type SubordinateEmployeeTableData = Pick<
  EmployeeSubordinateDto,
  | 'id'
  | 'firstName'
  | 'lastName'
  | 'avatarUrl'
  | 'companyName'
  | 'country'
  | 'contractLevel'
  | 'branchOfficeCode'
  | 'currencyForPayments'
  | 'replyEntryDate'
  | 'contractValidTo'
  | 'resultScore'
  | 'timezone'
  | 'careerPathEvaluation'
  | 'careerPath'
  | 'skillStatus'
> & {
  salary?: Pick<
    Salary,
    'id' | 'amount' | 'fromDate' | 'toDate' | 'isTentative' | 'isFulltime' | 'note' | 'percentageOfWorktime' | 'currency'
  > | null
} & {
  targetBonus?: Pick<TargetBonus, 'id' | 'amount' | 'fromDate' | 'toDate' | 'isTentative' | 'note' | 'currency'> | null
} & {
  businessUnit: Pick<BusinessUnit, 'id' | 'name'>
}

type ReducerTypes =
  | 'initFilter'
  | 'updateModalFilter'
  | 'updateInfoBoxFilter'
  | 'resetFilter'
  | 'setModalOpen'
  | 'updateEmployeeSkillStatus'
type EmployeesViewState = {
  data: SubordinateEmployeeTableData[]
  filteredData?: SubordinateEmployeeTableData[]
  filterResultLength?: number
  modalFilterActive?: boolean
  modalFormValues?: EmployeeModalFormDataType
  modalOpen?: boolean
}

export const EmployeesView: FC = () => {
  const { user } = useAuth()

  const [selected, companiesAndBu] = useEmployeeStore((state) => [state.selected, state.companiesAndBu], shallow)
  const isReviewer = !!companiesAndBu.filter((cbu) => cbu.companyId === selected.companyId && cbu.companyName === 'REVIEWER').length

  const {
    data,
    error: managerError,
    loading: managerLoading,
  } = useSubordinates(selected.companyId, isReviewer, selected.isAllBu ? null : selected.buId)
  if (managerLoading || managerError || !data || !data.subordinates) {
    return <LoadingAndErrors isLoading={managerLoading} hasNoData={!data || !data.subordinates} error={managerError} />
  }

  return (
    <EmployeesViewContent
      subordinates={data.subordinates}
      employeeLevels={data.employeeLevels}
      user={user}
      companyId={selected.companyId}
      buId={selected.isAllBu ? null : selected.buId}
      isReviewer={isReviewer}
    />
  )
}

const EmployeesViewContent: FC<{
  subordinates: SubordinateEmployeeTableData[]
  user: UserInfo
  employeeLevels: EmployeeLevelEnum[]
  companyId: string
  isReviewer: boolean
  buId: string | null
}> = ({ subordinates, user, employeeLevels, companyId, buId, isReviewer }) => {
  const [t] = useTranslation()
  const { data: branchOfficeVisibleFlags } = useGetCountriesWithBranchOfficeVisibleFlag()
  const showOfficeColumn = (branchOfficeVisibleFlags?.getCountriesWithBranchOfficeVisibleFlag ?? []).some(
    (item) => item.isBranchOfficeVisible && item.country === (user.country as Country),
  )

  const [viewMonetary, setViewMonetary] = useState<boolean>(false)
  const [infoBoxFilter, setInfoBoxFilter] = useState<EmployeeInfoBoxFilter>(employeeTabInitialFilter)

  const initialState: EmployeesViewState = {
    data: subordinates,
    filteredData: subordinates,
    filterResultLength: undefined,
    modalFilterActive: false,
    modalFormValues: emptyFormData,
    modalOpen: false,
  }

  useEffect(() => {
    dispatch({ type: 'initFilter', value: { data: subordinates } })
  }, [subordinates])

  useEffect(() => {
    dispatch({
      type: 'updateInfoBoxFilter',
      value: { data: state.data },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [infoBoxFilter])

  const setEmployeeSkillStatusData = (employeeId: string, skillStatusData?: SkillEmployeeStatusDto) => {
    if (skillStatusData) {
      const newSubordinates: SubordinateEmployeeTableData[] = state.data.map((el) => {
        if (el.id === employeeId) {
          return { ...el, skillStatus: skillStatusData }
        }
        return el
      })
      const filteredDataFromModalFilters: SubordinateEmployeeTableData[] = !!state.modalFilterActive
        ? filterTableData(newSubordinates, state.modalFormValues)
        : newSubordinates
      const filteredData: SubordinateEmployeeTableData[] = filterSubordinatesEmployeesFromInfoBoxFilters(
        filteredDataFromModalFilters,
        infoBoxFilter,
      )
      dispatch({
        type: 'updateEmployeeSkillStatus',
        value: { data: newSubordinates, filteredData: filteredData, filterResultLength: filteredData.length },
      })
    }
  }

  const reducer = (actState: EmployeesViewState, action: { type: ReducerTypes; value: EmployeesViewState }) => {
    const nextState = { ...actState }
    switch (action.type) {
      case 'updateModalFilter':
        nextState.filteredData = action.value.filteredData
        nextState.filterResultLength = action.value.filterResultLength
        nextState.modalFilterActive = action.value.modalFilterActive
        nextState.modalOpen = false
        nextState.modalFormValues = action.value.modalFormValues
        break
      case 'updateInfoBoxFilter':
        const filteredDataFromModalFilters: SubordinateEmployeeTableData[] = !!actState.modalFilterActive
          ? filterTableData(actState.data, actState.modalFormValues)
          : actState.data
        const filteredData: SubordinateEmployeeTableData[] = filterSubordinatesEmployeesFromInfoBoxFilters(
          filteredDataFromModalFilters,
          infoBoxFilter,
        )
        nextState.filteredData = filteredData
        nextState.filterResultLength = action.value.filterResultLength
        break
      case 'setModalOpen':
        nextState.modalOpen = action.value.modalOpen
        break
      case 'updateEmployeeSkillStatus':
        nextState.data = action.value.data
        nextState.filteredData = action.value.filteredData
        nextState.filterResultLength = action.value.filterResultLength
        break
      case 'initFilter':
        setInfoBoxFilter(employeeTabInitialFilter)
        return { ...initialState }
      case 'resetFilter':
        setInfoBoxFilter(employeeTabInitialFilter)
        return { ...initialState, data: action.value.data }
      default:
        throw new Error()
    }
    return nextState
  }
  const [state, dispatch] = useReducer(reducer, initialState)
  const hasResultScore = state.data.map((elem) => elem.resultScore).filter((resultScore) => resultScore).length > 0
  const hasCareerPathData = state.data.filter((elem) => elem.careerPathEvaluation).length > 0
  const possibleLevels: EmployeeLevelEnum[] = [...new Set(state.data.map((e) => e.contractLevel as EmployeeLevelEnum))]
  const possibleBUs: string[] = [...new Set(state.data.map((e) => e.businessUnit.name))]
  const atLeastOneExpiredOrUnknownSkillStatus: boolean = state.data.some((sub) => !sub.skillStatus || sub.skillStatus.expired)
  const oldestSyncUTCDate: string | undefined = getOldestSyncUTCDate(state.data)

  const onSubmit = (modalValues: EmployeeModalFormDataType) => {
    const filteredDataFromModalFilters: SubordinateEmployeeTableData[] = filterTableData(state.data, modalValues)
    const filteredData: SubordinateEmployeeTableData[] = filterSubordinatesEmployeesFromInfoBoxFilters(
      filteredDataFromModalFilters,
      infoBoxFilter,
    )
    dispatch({
      type: 'updateModalFilter',
      value: {
        data: state.data,
        filteredData: filteredData,
        filterResultLength: filteredData.length,
        modalFormValues: modalValues,
        modalFilterActive: JSON.stringify(modalValues) !== JSON.stringify(emptyFormData),
      },
    })
  }
  const onCancel = () => {
    dispatch({ type: 'setModalOpen', value: { data: state.data, modalOpen: false } })
  }
  const resetTableData = () => {
    dispatch({
      type: 'resetFilter',
      value: { data: state.data, modalFilterActive: false },
    })
  }
  const toggleMonetaryButton = viewMonetary ? (
    <PerInfoBoxActionButtonElement
      icon="view_fill"
      tooltip={t('employeeList.infoBox.toggleMonetary.button.hide')}
      color="primary"
      onClick={() => setViewMonetary(false)}
    />
  ) : (
    <PerInfoBoxActionButtonElement
      icon="viewNo_fill"
      tooltip={t('employeeList.infoBox.toggleMonetary.button.show')}
      color="secondary"
      onClick={() => setViewMonetary(true)}
    />
  )

  const mapEmployeesToCurrencyAndCountry = (employees: SubordinateEmployeeTableData[]): CurrencyAndCountry[] => {
    const modalData: CurrencyAndCountry[] = []
    employees.forEach((employee: SubordinateEmployeeTableData) => {
      if (employee.currencyForPayments && employee.contractLevel && employee.country) {
        modalData.push({
          currency: employee.currencyForPayments,
          country: employee.country,
        })
      }
    })
    return modalData
  }

  return (
    <>
      <EmployeeFilterModal
        filterModalData={mapEmployeesToCurrencyAndCountry(state.data)}
        modalFormValues={state.modalFormValues ? state.modalFormValues : emptyFormData}
        hasResultScore={hasResultScore}
        onSubmit={onSubmit}
        onCancel={onCancel}
        modalOpen={!!state.modalOpen}
        user={user}
        showOfficeFilter={showOfficeColumn}
        possibleOffices={possibleOffices(state.data)}
      />
      <EmployeeInfoBox
        hasCareerPathData={hasCareerPathData}
        resetTableData={resetTableData}
        filterResultLength={state.filteredData !== undefined ? state.filteredData.length : 0}
        setModalOpen={(value: boolean) => dispatch({ type: 'setModalOpen', value: { data: state.data, modalOpen: value } })}
        totalOfEmployees={state.data.length}
        toggleMonetaryButton={toggleMonetaryButton}
        infoBoxFilter={infoBoxFilter}
        setInfoBoxFilter={setInfoBoxFilter}
        modalFilterActive={!!state.modalFilterActive}
        possibleLevels={possibleLevels}
        possibleBUs={possibleBUs}
        employeeLevels={employeeLevels}
        atLeastOneExpiredOrUnknownSkillStatus={atLeastOneExpiredOrUnknownSkillStatus}
        isCompanySettings={false}
        companyId={companyId}
        buId={buId}
        isReviewer={isReviewer}
        oldestSyncUTCDate={oldestSyncUTCDate}
      />
      <EmployeeList
        listData={state.filteredData ? state.filteredData : []}
        updateEmployeeSkillStatus={(employeeId: string, skillStatusData?: SkillEmployeeStatusDto) =>
          setEmployeeSkillStatusData(employeeId, skillStatusData)
        }
        filterResultLength={state.filterResultLength}
        hasResultScore={hasResultScore}
        hasCareerPath={hasCareerPathData}
        user={user}
        viewMonetary={viewMonetary}
        showOfficeColumn={showOfficeColumn}
      />
    </>
  )
}
